import { TReducers } from '../../store/reducers'
import React from 'react'
import { useSelector } from 'react-redux'
import { Navigate, useLocation } from 'react-router-dom'
import { cookies } from '@zing/shared/cookies/behavior'
import { ROUTES } from '@zing/shared/constants'
import { accessOverride } from '@zing/active-tenant/auth/accessOverride'
import { getCredLocalStorage } from '@zing/shared/auth/credLocalStorage'

interface AuthRouteProps {
  features?: string[]
  element: JSX.Element
}

// Used as the `element` prop of a <Route>, not as a <Route> itself.
// React Router v6 requires routes to be declared as static <Route> JSX — custom
// components that return <Route> are invisible to the router's route tree builder.
export function AuthRoute({ features = [], element }: AuthRouteProps): JSX.Element {
  const isAuthenticated = useSelector((state: TReducers) => state.auth.isAuthenticated)
  const { pathname } = useLocation()
  let havePermission
  const userPermissions: string[] = []
  const userCookie = cookies.get('_perm')

  if (!isAuthenticated || cookies.get('_cred')?.data?.token === undefined) {
    return <Navigate to={ROUTES.USER_AUTH.LOGIN} />
  }

  // Computed unconditionally (before the `_perm` cookie check below) so a tenant override that
  // explicitly DENIES a route (e.g. Teia's meeting-room email gate) can't be bypassed by a
  // session that happens to have no `_perm` cookie — the block below skips ALL its checks
  // when that cookie is missing, which would otherwise let any authenticated user through.
  const override = accessOverride(features, pathname)
  if (override === false) {
    return <Navigate to="/not-found" replace />
  }

  if (userCookie !== undefined) {
    userCookie.features.forEach((permission) => {
      userPermissions.push(String(permission.code))
    })

    const companyType = getCredLocalStorage()?.company?.type
    const isAdminCityHall = userPermissions.includes('admin city hall') || userPermissions.includes('admin_city_hall') || companyType?.type === "Admin City Hall"
    const isCityHall = companyType?.type === "City Hall"

    if (override !== undefined) {
      havePermission = override
    } else if (isAdminCityHall && features.length > 0) {
      havePermission = features.some(feature => {
        const featureLower = feature.toLowerCase()
        return featureLower === 'admin city hall' ||
               userPermissions.includes(feature) ||
               userPermissions.includes(featureLower) ||
               userPermissions.some(perm => perm.toLowerCase() === featureLower)
      })
    } else if (isCityHall && features.length > 0) {
      havePermission = features.some(feature => {
        const featureLower = feature.toLowerCase()
        return featureLower === 'city hall' ||
               userPermissions.includes(feature) ||
               userPermissions.includes(featureLower) ||
               userPermissions.some(perm => perm.toLowerCase() === featureLower)
      })
    } else {
      havePermission = features?.some((feature) => userPermissions.includes(feature)) ?? false
    }

    if (!havePermission && features && features.length > 0) {
      return <Navigate to="/not-found" replace />
    }
  }

  return element
}
